import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import usePlacementDeZones from '../usePlacementDeZones';
import PageDeGabarit from './PageDeGabarit';
import PaletteDeChamps from './PaletteDeChamps';

/**
 * LE PLAN DU GABARIT : ses pages en images, et les zones qu'on y pose.
 *
 * C'est la surface de travail de l'atelier. Elle montre le PDF **BRUT** à dessein : on
 * pose un champ SOUS un libellé (« Nom : », « Adresse du site : »), et ces libellés
 * disparaissent sous les valeurs dans le rendu rempli. Poser sur le rendu rempli, ce
 * serait viser une cible déjà recouverte.
 *
 * ⚠️ L'ENREGISTREMENT EST EXPLICITE, PAS AUTOMATIQUE.
 *
 * Un enregistrement à chaque pixel figerait une version par mouvement de souris —
 * l'historique d'ATL-3 est append-only, il gonflerait de mille entrées en une séance. Et
 * surtout : tant qu'on n'a pas cliqué, on peut encore annuler. Le bouton dit combien de
 * changements attendent.
 */
export default function PlanDeGabarit({ gabaritId, plan, onEnregistre }) {
  // ⚠️ LA GÉOMÉTRIE VIENT DU PARENT, ELLE N'EST PLUS REDEMANDÉE ICI. Le rendu rempli en
  // a besoin aussi, et depuis que les deux panneaux restent montés, deux composants
  // appelaient `/pages` pour la même réponse. Voir `MandatTemplatePage`.
  const { pages, images, chargement, erreur } = plan;
  const [catalogue, setCatalogue] = useState([]);
  const [venuDuServeur, setVenuDuServeur] = useState([]);
  const [enregistrement, setEnregistrement] = useState(false);
  // Le grossissement du plan. 1 = la page entière tient dans la hauteur ; au-delà, elle
  // dépasse et la surface défile. Indispensable au doigt : une A4 entière sur une
  // tablette met un champ de mandat à trois millimètres.
  const [zoom, setZoom] = useState(1);
  const [reprises, setReprises] = useState([]);
  const [repriseDe, setRepriseDe] = useState(null);
  const depot = useRef(null);
  const surface = useRef(null);
  const [enMain, setEnMain] = useState(null);

  // Les lignes de texte de chaque page, pour l'aimantation. Elles viennent du serveur,
  // en fractions : l'écran ne convertit rien.
  const lignesParPage = useMemo(() => Object.fromEntries(
    (pages || []).map((p) => [p.numero, p.lignes_y || []])), [pages]);

  const placement = usePlacementDeZones(venuDuServeur, { lignesParPage });
  const { zones, selection, poser, annuler, auClavier, poserZones } = placement;

  const libelles = useMemo(() => Object.fromEntries(
    catalogue.map((c) => [c.cle, c.libelle])), [catalogue]);

  // Ce que chaque zone écrit à l'écran pendant qu'on la pose. Le serveur les envoie avec
  // le catalogue : une seconde liste tenue ici divergerait au premier champ ajouté, et
  // c'est exactement l'écart qui a produit la double écriture du 07/09.
  const exemples = useMemo(() => Object.fromEntries(
    catalogue.map((c) => [c.cle, c.exemple])), [catalogue]);

  const modifie = useMemo(
    () => JSON.stringify(zones) !== JSON.stringify(venuDuServeur),
    [zones, venuDuServeur]);

  // ---- Ce que le serveur sait déjà de ce gabarit ----
  useEffect(() => {
    if (!gabaritId) return undefined;
    let vivant = true;
    setRepriseDe(null);
    api.get(`/admin/mandat-templates/${gabaritId}/zones`)
      .then(({ data }) => {
        if (!vivant) return;
        setCatalogue(data.champs_posables || []);
        setVenuDuServeur(data.zones || []);
        // Aucune zone posée : peut-être y en a-t-il à reprendre d'un gabarit remplacé.
        if (!(data.zones || []).length) {
          api.get(`/admin/mandat-templates/${gabaritId}/zones/reprises`)
            .then(({ data: r }) => { if (vivant) setReprises(r.candidats || []); })
            .catch(() => { if (vivant) setReprises([]); });
        } else {
          setReprises([]);
        }
      })
      .catch(() => { if (vivant) toast.error("Les zones n'ont pas pu être chargées"); });
    return () => { vivant = false; };
  }, [gabaritId]);

  // ---- Le dépôt d'une pastille : commencé sur la palette, terminé sur la page ----
  //
  // ⚠️ LE `pointerup` EST ÉCOUTÉ SUR `window`, PAS SUR LA PAGE.
  //
  // Première version : `onPointerUp` sur le conteneur de la page. Elle ne recevait jamais
  // l'événement — le pointeur a été pressé sur le bouton de la palette, et c'est LUI qui
  // reste la cible des événements suivants, page ou pas. Mesuré en recette : la zone ne se
  // posait pas, sans la moindre erreur.
  //
  // On écoute donc au niveau de la fenêtre et on demande à `elementFromPoint` sur quelle
  // page le doigt s'est levé. C'est aussi ce qui rend le dépôt correct sur un gabarit de
  // plusieurs pages : le numéro vient de la page réellement survolée, pas d'une supposition.
  const commencerDepot = useCallback((evenement, champ) => {
    evenement.preventDefault();
    depot.current = champ;
    // ⚠️ CE QU'ON TIENT EN MAIN DOIT SE VOIR. Entre le `pointerdown` sur la palette et
    // le `pointerup` sur la page, RIEN ne bougeait à l'écran : ni image fantôme, ni
    // curseur, ni surbrillance. On traversait la page sans savoir si le champ était
    // attrapé, et un lâcher hors d'une page ne disait rien non plus. C'est la moitié du
    // « il n'est pas fluide de poser les variables sur le PDF » du 07/09 — l'autre
    // moitié étant qu'on ne voyait pas où la valeur allait tomber.
    setEnMain({ champ, x: evenement.clientX, y: evenement.clientY });
  }, []);

  // La pastille suit le pointeur tant qu'on la tient. On écoute la FENÊTRE et non la
  // page, pour la même raison que le `pointerup` ci-dessous : le pointeur a été pressé
  // sur le bouton de la palette, et c'est lui qui reste la cible des événements suivants.
  useEffect(() => {
    const suivre = (evenement) => {
      if (!depot.current) return;
      setEnMain({ champ: depot.current, x: evenement.clientX, y: evenement.clientY });
    };
    window.addEventListener('pointermove', suivre);
    return () => window.removeEventListener('pointermove', suivre);
  }, []);

  useEffect(() => {
    const relacher = (evenement) => {
      const champ = depot.current;
      depot.current = null;
      setEnMain(null);
      if (!champ) return;

      const sous = document.elementFromPoint(evenement.clientX, evenement.clientY);
      const conteneur = sous?.closest?.('[data-page-numero]');
      const image = conteneur?.querySelector('img');
      if (!conteneur || !image) return;

      // ⚠️ On mesure l'IMAGE, jamais le conteneur : il porte une bordure et un rayon, et
      // deux ou trois pixels d'écart décalent la zone d'un demi-millimètre sur le PDF.
      const cadre = image.getBoundingClientRect();
      if (!cadre.width || !cadre.height) return;
      const x = (evenement.clientX - cadre.left) / cadre.width;
      const y = (evenement.clientY - cadre.top) / cadre.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) return;

      poser(champ, x, y, Number(conteneur.dataset.pageNumero) || 0);
    };
    window.addEventListener('pointerup', relacher);
    return () => window.removeEventListener('pointerup', relacher);
  }, [poser]);

  // ---- Le clavier vit sur la surface, pas sur le document ----
  useEffect(() => {
    const noeud = surface.current;
    if (!noeud) return undefined;
    noeud.addEventListener('keydown', auClavier);
    return () => noeud.removeEventListener('keydown', auClavier);
  }, [auClavier]);

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const { data } = await api.put(`/admin/mandat-templates/${gabaritId}/zones`,
                                     { zones });
      setVenuDuServeur(data.zones || []);
      setRepriseDe(null);
      toast.success(`${(data.zones || []).length} zone(s) enregistrée(s)`
                    + (data.zones_version ? ` · version ${data.zones_version}` : ''));
      onEnregistre?.(data);
    } catch (e) {
      // Le serveur nomme la zone fautive et sa raison : on affiche son message, pas le
      // nôtre. « Données invalides » devant un plan de douze champs n'apprend rien.
      toast.error(e.response?.data?.detail || "Les zones n'ont pas pu être enregistrées");
    }
    setEnregistrement(false);
  };

  if (erreur) {
    return (
      <div className="h-full flex items-center justify-center gap-2 text-[12px] text-amber-800"
           data-testid="gabarit-plan-erreur">
        <AlertTriangle className="w-4 h-4 text-amber-600" /> {erreur}
      </div>
    );
  }

  if (!pages.length) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-neutral-400"
           data-testid="gabarit-plan-vide">
        {chargement
          ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Rendu du plan…</>
          : 'Aucune page à afficher'}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col lg:flex-row gap-2" data-testid="gabarit-plan">
      {/* Sous `lg`, la colonne de gauche devient une BARRE horizontale au-dessus du
          plan : une colonne de 150 px sur un téléphone mange la moitié de la largeur, et
          la page à côté devient illisible. */}
      <div className="w-full lg:w-[150px] shrink-0 lg:overflow-y-auto pr-1 pt-1">
        <PaletteDeChamps
          champs={catalogue}
          dejaPosees={zones.map((z) => z.champ)}
          onCommencerDepot={commencerDepot}
        />
        {/* Empile sur ordinateur, EN LIGNE sur tablette : la colonne de gauche devient une
            barre, et trois boutons pleine largeur les uns sous les autres y mangeraient
            autant de place que le document. */}
        <div className="mt-2 lg:mt-3 flex lg:block items-center gap-1.5 lg:gap-0 lg:space-y-1.5
                        border-t-0 lg:border-t border-neutral-200 pt-0 lg:pt-2">
          <Button size="sm" variant={modifie ? 'default' : 'outline'}
                  className="w-full text-[11px] h-7" disabled={!modifie || enregistrement}
                  onClick={enregistrer} data-testid="zones-enregistrer">
            <Save className="w-3 h-3 mr-1" />
            {enregistrement ? 'Enregistrement…' : modifie ? 'Enregistrer' : 'À jour'}
          </Button>
          <Button size="sm" variant="outline" className="w-full text-[11px] h-7"
                  onClick={annuler} data-testid="zones-annuler">
            <RotateCcw className="w-3 h-3 mr-1" /> Annuler
          </Button>
          <div className="flex items-center gap-1 pt-0 lg:pt-1 shrink-0" data-testid="gabarit-zoom">
            <Button size="sm" variant="outline" className="flex-1 h-7 text-[13px] leading-none"
                    onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 2) / 2))}
                    disabled={zoom <= 1} aria-label="Réduire le plan"
                    data-testid="gabarit-zoom-moins">−</Button>
            <span className="text-[10px] text-neutral-400 w-9 text-center tabular-nums"
                  data-testid="gabarit-zoom-valeur">{Math.round(zoom * 100)} %</span>
            <Button size="sm" variant="outline" className="flex-1 h-7 text-[13px] leading-none"
                    onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.5) * 2) / 2))}
                    disabled={zoom >= 4} aria-label="Agrandir le plan"
                    data-testid="gabarit-zoom-plus">+</Button>
          </div>
          <p className="hidden lg:block text-[9.5px] text-neutral-400 leading-tight pt-0.5">
            La valeur s&apos;écrit SUR le trait du bas de chaque cadre.
          </p>
          {/* Le rappel du clavier n a rien a dire a un doigt. */}
          <p className="hidden lg:block text-[9.5px] text-neutral-400 leading-tight pt-0.5">
            Flèches : 1 px · Maj+flèches : 10 px · Alt : sans aimant · Suppr : retirer
          </p>
        </div>
      </div>

      {/* ⚠️ `tabIndex` sur la surface, pas sur le document : les flèches ne doivent
          déplacer une zone que lorsque le plan a le focus, sinon elles font défiler
          l'écran pendant qu'on croit bouger un champ. */}
      <div ref={surface} tabIndex={0}
           className="flex-1 min-h-0 overflow-auto p-2 outline-none rounded
                      flex flex-col items-center gap-3
                      focus:ring-1 focus:ring-marque/40"
           data-zoom={zoom}
           data-testid="gabarit-plan-surface">
        {repriseDe && (
          <div className="rounded-md border border-amber-200 bg-amber-50/70 p-2 flex items-start gap-2"
               data-testid="zones-bandeau-reprise">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11.5px] text-amber-900">
              Ces zones viennent de <strong>{repriseDe}</strong> et ne sont
              {' '}<strong>pas encore enregistrées</strong>. Vérifiez leur position sur ce
              document avant d&apos;enregistrer : deux PDF du même nom ne sont pas la même page.
            </p>
          </div>
        )}

        {!zones.length && reprises.length > 0 && (
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-2"
               data-testid="zones-reprises">
            <p className="text-[11.5px] text-neutral-600 mb-1.5">
              Ce modèle n&apos;a aucune zone. Reprendre celles d&apos;un modèle de même format ?
            </p>
            {reprises.map((c) => (
              <button
                type="button" key={c.id}
                onClick={() => { poserZones(c.zones); setRepriseDe(c.nom_fichier); }}
                className="text-[11.5px] text-marque underline block"
                data-testid={`zones-reprendre-${c.id}`}
              >
                Reprendre les {c.zones.length} zone(s) de « {c.nom_fichier} »
              </button>
            ))}
          </div>
        )}

        {pages.map((page) => (
          <PageDeGabarit
            key={page.numero}
            page={{ ...page, total: pages.length }}
            zoom={zoom}
            image={images[page.numero]}
            zones={zones}
            libelles={libelles}
            exemples={exemples}
            selection={selection}
            placement={placement}
          />
        ))}
      </div>

      {/* La pastille qu'on tient. `fixed` et non `absolute` : le pointeur peut sortir de
          la surface, et une pastille qui décrocherait au bord dirait le contraire de ce
          qu'elle est censée dire. */}
      {enMain && (
        <div
          className="fixed z-50 pointer-events-none text-[11px] px-1.5 py-0.5 rounded
                     bg-marque text-white shadow-sm whitespace-nowrap"
          style={{ left: enMain.x + 10, top: enMain.y + 10 }}
          data-testid="pastille-en-main"
        >
          {libelles[enMain.champ] || enMain.champ}
        </div>
      )}
    </div>
  );
}
